import React from 'react';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { EyeIcon, DownloadSimpleIcon } from '@phosphor-icons/react';
import { MPFSectionHeading } from 'volto-ministerio-publico-federal/components/UI';
import { getFileType } from 'volto-ministerio-publico-federal/utils/getFileType';
import { isAudioMime } from 'volto-ministerio-publico-federal/utils/isAudioMime';
import { isValidDate } from 'volto-ministerio-publico-federal/utils/isValidDate';
import useFormattedDate from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import {
  FileAudioIcon,
  FilePDFIcon,
  FileDOCIcon,
  FileODTIcon,
  FileRTFIcon,
  FileTXTIcon,
  FileXLSIcon,
  FileXLSXIcon,
  FileODSIcon,
  FileCSVIcon,
  FilePPTIcon,
  FilePPTXIcon,
  FileODPIcon,
  FileGIFIcon,
  FileJPEGIcon,
  FileJPGIcon,
  FilePNGIcon,
  FileSVGIcon,
  FileWEBPIcon,
  File7ZIcon,
  FileRARIcon,
  FileTARIcon,
  FileZIPIcon,
  FileJSONIcon,
  FileXMLIcon,
  FileGenericIcon,
} from 'volto-ministerio-publico-federal/components/Icons/Attachments';

const MODIFICATION_THRESHOLD_MS = 60 * 1000;

type FileIconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

const FILE_ICONS_BY_TYPE: Record<string, FileIconComponent> = {
  PDF: FilePDFIcon,
  DOC: FileDOCIcon,
  DOCX: FileDOCIcon,
  ODT: FileODTIcon,
  RTF: FileRTFIcon,
  TXT: FileTXTIcon,
  XLS: FileXLSIcon,
  XLSX: FileXLSXIcon,
  ODS: FileODSIcon,
  CSV: FileCSVIcon,
  PPT: FilePPTIcon,
  PPTX: FilePPTXIcon,
  ODP: FileODPIcon,
  JPG: FileJPGIcon,
  JPEG: FileJPEGIcon,
  PNG: FilePNGIcon,
  GIF: FileGIFIcon,
  WEBP: FileWEBPIcon,
  SVG: FileSVGIcon,
  ZIP: FileZIPIcon,
  RAR: FileRARIcon,
  'X-RAR': FileRARIcon,
  '7Z': File7ZIcon,
  TAR: FileTARIcon,
  JSON: FileJSONIcon,
  XML: FileXMLIcon,
};

const getFileIcon = (fileType: string): FileIconComponent =>
  FILE_ICONS_BY_TYPE[fileType] || FileGenericIcon;

interface AttachmentItem {
  '@id'?: string;
  '@type'?: string;
  title?: string;
  getObjSize?: string;
  mime_type?: string;
  type_title?: string;
  effective?: string;
  modified?: string;
  created?: string;
  Subject?: string[];
  [key: string]: unknown;
}

interface AttachmentsProps {
  items?: AttachmentItem[];
  listClassName?: string;
  title?: string;
}

export const Attachments: React.FC<AttachmentsProps> = ({
  items = [],
  listClassName = 'mpf__attachments-list',
  title,
}) => {
  const attachments = items.filter(
    (item) => item['@type'] === 'File' || item.mime_type?.includes('pdf'),
  );
  const { formatLong } = useFormattedDate();
  const isLoggedIn = useSelector(
    (state: { userSession?: { token?: string | null } }) =>
      !!state.userSession?.token,
  );

  const getDisplayDate = (
    item: AttachmentItem,
  ): { value: string; isModified: boolean } | null => {
    const publishedValue = isValidDate(item.effective)
      ? item.effective
      : item.created;
    if (!isValidDate(publishedValue)) return null;

    const publishedTime = new Date(publishedValue as string).getTime();
    const wasModified =
      isValidDate(item.modified) &&
      new Date(item.modified as string).getTime() - publishedTime >
        MODIFICATION_THRESHOLD_MS;

    if (wasModified) {
      return { value: item.modified as string, isModified: true };
    }
    return { value: publishedValue as string, isModified: false };
  };

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="mpf__attachments">
      {title && <MPFSectionHeading title={title} />}

      <ul className={listClassName}>
        {attachments.map((item, index) => {
          if (!item['@id']) {
            return null;
          }

          const downloadHref = `${item['@id']}/@@download/file`;
          const showPdfInline =
            !isLoggedIn && !!item.mime_type?.includes('pdf');

          const fileType = getFileType(item.mime_type);
          const FileTypeIcon = getFileIcon(fileType);
          const displayDate = getDisplayDate(item);

          return (
            <li key={index} className="mpf__attachment-item">
              <div className="mpf__attachment-link">
                <span
                  className={`mpf__attachment-icon mpf__attachment-icon--${fileType}`}
                  aria-hidden="true"
                >
                  {isAudioMime(item.mime_type) ? (
                    <FileAudioIcon extension={fileType} />
                  ) : (
                    <FileTypeIcon />
                  )}
                </span>

                <span className="mpf__attachment-info">
                  {item.Subject && item.Subject.length > 0 && (
                    <span className="mpf__attachment-subjects">
                      {item.Subject.map((subject, idx) => (
                        <UniversalLink
                          key={idx}
                          item={{
                            '@id': `/search?Subject=${encodeURIComponent(subject)}`,
                          }}
                          className="mpf__attachment-subject"
                        >
                          {subject}
                        </UniversalLink>
                      ))}
                    </span>
                  )}

                  <span className="mpf__attachment-title">{item.title}</span>

                  <span className="mpf__attachment-meta">
                    <span>.{fileType}</span>

                    {item.getObjSize && (
                      <>
                        <span
                          aria-hidden="true"
                          className="mpf__attachment-meta-separator"
                        >
                          •
                        </span>
                        <span>{item.getObjSize}</span>
                      </>
                    )}

                    {displayDate && (
                      <>
                        <span
                          aria-hidden="true"
                          className="mpf__attachment-meta-separator"
                        >
                          •
                        </span>
                        <time dateTime={displayDate.value}>
                          {displayDate.isModified && 'Atualizado em '}
                          {formatLong(displayDate.value)}
                        </time>
                      </>
                    )}
                  </span>
                </span>

                <span className="mpf__attachment-actions">
                  {showPdfInline ? (
                    <a
                      href={`${flattenToAppURL(item['@id'])}/@@display-file/file`}
                      className="mpf__attachment-action"
                      title={`Visualizar ${item.title}`}
                      aria-label={`Visualizar ${item.title}`}
                    >
                      <EyeIcon size={24} />
                    </a>
                  ) : (
                    <UniversalLink
                      item={{ '@id': item['@id'] }}
                      className="mpf__attachment-action"
                      title={`Visualizar ${item.title}`}
                      aria-label={`Visualizar ${item.title}`}
                    >
                      <EyeIcon size={24} />
                    </UniversalLink>
                  )}

                  <UniversalLink
                    item={{ '@id': downloadHref }}
                    className="mpf__attachment-action"
                    download
                    title={`Baixar ${item.title}`}
                    aria-label={`Baixar ${item.title}`}
                  >
                    <DownloadSimpleIcon size={24} />
                  </UniversalLink>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Attachments;
